import { FilePicker, type PickedFile } from "@capawesome/capacitor-file-picker";
import { isDefined } from "@clipboard-health/util-ts";

import { APP_V2_APP_EVENTS, logError } from "../analytics";
import {
  base64ToBlob,
  blobToBase64,
  getFileTypeFromBlob,
  isAcceptableFileUploadContentType,
} from "../files";
import { isAcceptableMSWordUploadContentType } from "../files/utils";
import { type UploadFileData } from "./types";

interface MobileMultiFileSelectProps {
  onSuccess: (files: UploadFileData[]) => void;
  onError: (error: Error) => void;
  allowMultiple?: boolean;
  contentTypes: string[];
}

/**
 * Converts a PickedFile from @capawesome/capacitor-file-picker to UploadFileData format
 */
async function convertPickedFileToUploadFileData(pickedFile: PickedFile): Promise<UploadFileData> {
  const { data, mimeType, blob, name } = pickedFile;

  // Validate content type first
  if (!isAcceptableFileUploadContentType(mimeType)) {
    throw new Error(`Unsupported file type: ${mimeType}`);
  }

  let fileBlob: Blob;

  // Handle different data formats from FilePicker
  if (blob) {
    // Web platform provides blob directly
    fileBlob = blob;
  } else if (data) {
    // Handle both string and Uint8Array data formats
    if (typeof data === "string") {
      // Mobile platforms provide base64 string
      const dataUrlBase64 = `data:${mimeType};base64,${data}`;
      fileBlob = await base64ToBlob(dataUrlBase64);
    } else {
      // Handle Uint8Array data
      fileBlob = new Blob([data], { type: mimeType });
    }
  } else {
    throw new Error("No file data available in picked file");
  }

  // Convert blob to base64 data URL
  const dataUrlBase64 = await blobToBase64(fileBlob);

  // Get file extension from blob analysis (more reliable than name parsing)
  const fileType = await getFileTypeFromBlob(fileBlob);
  const { extension } = fileType;

  return {
    contentType: mimeType,
    dataUrlBase64,
    extension,
    blob: fileBlob,
    name,
  };
}

export function useMobileMultiFileSelect(props: MobileMultiFileSelectProps): () => Promise<void> {
  const { onSuccess, onError, allowMultiple = true, contentTypes } = props;

  return async () => {
    try {
      const result = await FilePicker.pickFiles({
        types: contentTypes,
        limit: allowMultiple ? 0 : 1, // 0 means unlimited, 1 means single file
        readData: true,
      });

      // This means the user has cancelled selection
      if (!isDefined(result) || !isDefined(result.files) || result.files.length === 0) {
        return;
      }

      const { files: pickedFiles } = result;

      // Validate all files before processing
      const invalidFiles = pickedFiles.filter(
        (file) => !isAcceptableFileUploadContentType(file.mimeType)
      );

      if (invalidFiles.length > 0) {
        const invalidFileTypes = invalidFiles.map((file) => file.mimeType).join(", ");
        throw new Error(`Files with unsupported content types picked: ${invalidFileTypes}`);
      }

      if (
        pickedFiles.some((file) => isAcceptableMSWordUploadContentType(file.mimeType)) &&
        pickedFiles.length > 1
      ) {
        throw new Error(
          `Multiple files aren't allowed with a document. Remove the document or upload it alone`
        );
      }

      // Convert all picked files to UploadFileData format
      const uploadFileDataArray = await Promise.all(
        pickedFiles.map(async (pickedFile) => await convertPickedFileToUploadFileData(pickedFile))
      );

      onSuccess(uploadFileDataArray);
    } catch (error) {
      logError(APP_V2_APP_EVENTS.DOCUMENT_UPLOAD_MOBILE_FILE_PICKER_FAILURE, {
        error,
      });
      onError(error as Error);
    }
  };
}
